/**
 * Write gate for a program's buildState, shared by the edit-lock provider and
 * the builders' persistence effects.
 *
 * The lock provider publishes its status per program here; ProgramCreationContext
 * (which sits ABOVE the manual builder's lock provider, so it can't read the
 * context) and the AIMD wizard ask `isBuildStateWritable(grpId)` before every
 * buildState save. A program is writable when nothing is tracking it, when the
 * lock is held, or when nothing lockable is on screen. It is NOT writable while
 * the lock is being acquired (the copy on screen may be stale), while another
 * admin holds it, or while a reload from the BE is in flight — so a read-only
 * or stale snapshot never overwrites the editor's saved work.
 */
import { useSyncExternalStore } from 'react';
import type { EditLockStatus } from './ProgramEditLockProvider';

const statusByProgram = new Map<string, EditLockStatus>();
const hydratingPrograms = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

export const setEditLockGateStatus = (grpId: string, status: EditLockStatus | null) => {
  if (!grpId) return;
  if (status === null) statusByProgram.delete(grpId);
  else statusByProgram.set(grpId, status);
  notify();
};

/** Mark a program as being reloaded from the BE; saves wait until it clears. */
export const setBuildStateHydrating = (grpId: string, hydrating: boolean) => {
  if (!grpId) return;
  if (hydrating) hydratingPrograms.add(grpId);
  else hydratingPrograms.delete(grpId);
  notify();
};

export const isBuildStateWritable = (grpId?: string | null): boolean => {
  if (!grpId) return true;
  if (hydratingPrograms.has(grpId)) return false;
  const status = statusByProgram.get(grpId);
  return !status || status === 'held' || status === 'idle';
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Reactive form of isBuildStateWritable — re-renders when the gate changes. */
export const useBuildStateWritable = (grpId?: string | null): boolean =>
  useSyncExternalStore(subscribe, () => isBuildStateWritable(grpId));
